/** @file clip.setPose: one full-body pose at one frame (the key's events are kept). */
import type { Ease, NodeKey } from '@zilo/pose3d-format/types/clip';
import { checkEnum } from '@zilo/pose3d-format/validate/fields/checkEnum';
import type { CommandDef } from '../apply/commandDef';
import { findClip } from '../puppetDoc/findClip';
import { compact } from '../validate/compact';
import { readInput } from '../validate/readInput';
import { NODE_KEYS_CHECK } from './nodeKeysCheck';
import { writePose } from './writePose';

const EASE = checkEnum(['linear', 'easeIn', 'easeOut', 'easeInOut', 'step']);

/** `clip.setPose { clip, frame, nodes, ease? }` → `{ frame }`: replaces the key's nodes; inverse: the previous key. */
export const clipSetPoseCommand: CommandDef<'clip.setPose'> = {
  name: 'clip.setPose',
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => {
    const r = readInput(input, path, ['clip', 'frame', 'nodes', 'ease']);
    return compact({
      clip: r.str('clip'), frame: r.int('frame', { min: 0 }), nodes: r.check('nodes', NODE_KEYS_CHECK) as Record<string, NodeKey>,
      ease: r.opt('ease', (k) => r.check(k, EASE) as Ease),
    });
  },
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input) => {
    const { name } = findClip(draft, input.clip);
    return writePose(draft, compact({ clipName: name, frame: input.frame, nodes: input.nodes, merge: false, ease: input.ease }));
  },
};
